import {
  defaultLoadableState,
  type LoadableState,
  useLoadableState
} from '@carvana/merch-react-hooks';
import deepmerge from 'deepmerge';
import React, { createContext, useContext, useMemo } from 'react';
import TagIds from '~/constants/common/tagIds';
import { InventoryTypeKey } from '~/constants/inventoryType';
import PhotoInventoryType from '~/constants/PhotoinventoryType';
import Store from '~/constants/store';
import type { Recall, VehicleDetails } from '~/contracts/vdp';
import { transformExteriorImages } from '~/transform/imagery';
import type { DeepPartial } from '~/types';

export type ContextType = {
  recalls: LoadableState<Recall[]>;
  vehicleDetails: Partial<VehicleDetails>;
};

const defaultContext: ContextType = {
  recalls: defaultLoadableState<Recall[]>([]),
  vehicleDetails: {}
};

export const VehicleContext = createContext<ContextType>(defaultContext);

export type OwnProps = React.PropsWithChildren<{
  vehicleDetails: Partial<VehicleDetails>;
}>;

export const VehicleContextProvider = ({
  children,
  vehicleDetails
}: OwnProps) => {
  const recalls = useLoadableState<Recall[]>(defaultContext.recalls.data);

  const value = useMemo<ContextType>(
    () => ({ recalls, vehicleDetails }),
    [recalls, vehicleDetails]
  );

  return (
    <VehicleContext.Provider value={value}>{children}</VehicleContext.Provider>
  );
};

VehicleContextProvider.displayName = 'VehicleContextProvider';

export default VehicleContext;

export const generateVehicleContext = (
  customState: DeepPartial<ContextType>
): ContextType =>
  deepmerge<ContextType, DeepPartial<ContextType>>(defaultContext, customState);

export function useRecalls() {
  return useContext(VehicleContext).recalls.data;
}

export function useGetVehicleDetails() {
  return useContext(VehicleContext).vehicleDetails;
}

export function useGetVehicleId() {
  return useGetVehicleDetails().vehicleId ?? 0;
}

export function useGetVehicleStockNumber() {
  return useGetVehicleDetails().stockNumber ?? 0;
}

export function useGetVehicleIdAndStockNumber() {
  return {
    stockNumber: useGetVehicleStockNumber(),
    vehicleId: useGetVehicleId()
  };
}

export function useGetVehicleInfoForSpinner() {
  const { installedPackages = [], make } = useGetVehicleDetails();

  return { installedPackages, make };
}

/**
 * Determines if a vehicle is a WIP or not. Very rarely do you need this
 * information. Only in cases of imagery should this be used. In most cases you
 * should use `useGetIsReservableVehicle` to determine if a vehicle is
 * reservable or not.
 */
export function useGetIsWIPVehicle() {
  return useGetVehicleDetails().inventoryType === InventoryTypeKey.WIP;
}

export function useGetActivationModalVehicleData() {
  const stockNumber = useGetVehicleStockNumber();
  const {
    make = '',
    mileage = 0,
    model = '',
    price = 0,
    trim = '',
    vehicleId = 0,
    stockImageData,
    year = 0
  } = useGetVehicleDetails();
  const isWIPVehicle = useGetIsWIPVehicle();

  return {
    imageUrl: isWIPVehicle
      ? stockImageData?.jellyBeanDesktopUrl ?? ''
      : `/${stockNumber}/post-large/${stockNumber}-edc-02-search.jpg`,
    isOnDemand: isWIPVehicle,
    make,
    mileage,
    model,
    price: { total: price },
    stockNumber,
    trim,
    vehicleId,
    vehicleLockType: 0,
    year
  };
}

export function useGetIsRecentlyAdded() {
  return (
    useGetVehicleDetails().highlights?.some(
      ({ tagId }) => tagId === TagIds.RecentlyAdded
    ) || false
  );
}

export function useGetIsLightAccidentVehicle() {
  return useGetVehicleDetails().stores?.includes(Store.LightAccident) ?? false;
}

export function useGetSpinnerData() {
  return useGetVehicleDetails().vexVdpImageData || {};
}

export function useGetHasImages() {
  const { features = [], imperfections = [], spins = [] } = useGetSpinnerData();

  return Boolean(
    features.length ||
      imperfections.length ||
      transformExteriorImages(spins).length
  );
}

export function useGetIsDealerMarketplace() {
  const { inventoryType, stores } = useGetVehicleDetails();

  return (
    inventoryType === InventoryTypeKey.DealerMarketplace ||
    (stores?.includes(Store.DMP2Core) ?? false)
  );
}

export function useGetIs3PR() {
  const { stores = [] } = useGetVehicleDetails();

  return stores.includes(Store.Manheim);
}

export function useGetIsPartnerRetailVehicle() {
  const { stores = [] } = useGetVehicleDetails();

  return stores.includes(Store.PartnerRetail);
}

export function useGetIsPartnerVehicle() {
  const isMarketplace = useGetIsDealerMarketplace();
  const is3PR = useGetIs3PR();

  return isMarketplace || is3PR;
}

export function useGetIsMobileCapture() {
  const isPartnerVehicle = useGetIsPartnerVehicle();
  const { vexVdpImageData } = useGetVehicleDetails();

  return (
    isPartnerVehicle ||
    vexVdpImageData?.captureInfo?.inventoryType !== PhotoInventoryType.Core
  );
}

export function useGetLastModified() {
  return (
    useGetVehicleDetails().vexVdpImageData?.captureInfo?.lastModified ?? ''
  );
}
